import { addDays, parseISO, subDays, subMonths, subYears } from "date-fns";
import i18next from "i18next";

export const countriesList = [
  {
    name: "United States",
    alpha_2: "US",
  },
  {
    name: "Canada",
    alpha_2: "CA",
  },
  {
    name: "United Kingdom",
    alpha_2: "GB",
  },
  {
    name: "Australia",
    alpha_2: "AU",
  },
];

export const getUserCountry = (): string => i18next.language.split("-")[1];

export type Residence = {
  start_date: string;
  end_date: string | null;
  address: Partial<{
    country: string;
    postal_code: string;
    administrative_division_1: string;
    locality: string;
    street_address: string;
  }>;
};

function getPeriodStart(requirement: {
  type: "months" | "years" | "days";
  value: number;
}) {
  return addDays(
    requirement.type === "years"
      ? subYears(Date.now(), requirement.value)
      : requirement.type === "months"
        ? subMonths(Date.now(), requirement.value)
        : subDays(Date.now(), requirement.value),
    1 // We want to include today in the period
  );
}

function getTransformedResidences(residences: Residence[], periodStart: Date) {
  return residences
    .map((residence) => ({
      ...residence,
      start_date: parseISO(residence.start_date),
      end_date: residence.end_date
        ? parseISO(residence.end_date)
        : new Date(Date.now()),
    }))
    .sort((a, b) => a.end_date.getTime() - b.end_date.getTime())
    .filter((residence) => residence.end_date >= periodStart);
}

export function checkResidencesMeetRequirementsForPeriod(
  residences: Residence[],
  requirement: {
    type: "months" | "years" | "days";
    value: number;
  }
) {
  const hasCurrentAddress = residences.some(({ end_date }) => !end_date);

  const periodStart = getPeriodStart(requirement);

  const transformedResidences = getTransformedResidences(
    residences,
    periodStart
  );

  const hasAResidenceThatStartedOnOrBeforePeriod = transformedResidences.some(
    (residence) => residence.start_date <= periodStart
  );

  const hasGaps = transformedResidences.some((residence, index) => {
    if (index === 0) {
      return false;
    }
    const previousResidence = transformedResidences[index - 1];
    return addDays(previousResidence.end_date, 1) < residence.start_date;
  });

  return (
    hasCurrentAddress && hasAResidenceThatStartedOnOrBeforePeriod && !hasGaps
  );
}

export function checkResidencesMeetRequirementsForCountry(
  residences: Residence[],
  when: {
    months?: number;
    days?: number;
  },
  countryCode: string
) {
  const periodStart = getPeriodStart(
    when.months
      ? { type: "months", value: when.months }
      : { type: "days", value: when.days || 1 }
  );
  const transformedResidences = getTransformedResidences(
    residences,
    periodStart
  );

  return transformedResidences.some(
    (residence) => residence.address.country === countryCode
  );
}

const GB_SUBDIVISION_TO_UK_REGION: Record<string, string> = {
  ENG: "ENG",
  WLS: "WLS",
  NIR: "NIR",
  SCT: "SCT",
  // Northern Ireland districts
  ABC: "NIR",
  AND: "NIR",
  ANN: "NIR",
  BFS: "NIR",
  CCG: "NIR",
  DRS: "NIR",
  FMO: "NIR",
  LBC: "NIR",
  MEA: "NIR",
  MUL: "NIR",
  NMD: "NIR",
};

function resolveGbAdminDivisionToUkRegion(
  adminDiv: string | undefined
): string | undefined {
  if (!adminDiv) return undefined;
  return GB_SUBDIVISION_TO_UK_REGION[adminDiv];
}

function getCoveredLocationCodesForAddress(
  address: Residence["address"] | undefined
): string[] {
  const country = address?.country?.toUpperCase();
  const codes: string[] = [];

  if (country) {
    codes.push(country);
  }

  if (country === "GB") {
    const ukRegion = resolveGbAdminDivisionToUkRegion(
      address?.administrative_division_1?.toUpperCase()
    );
    if (ukRegion) {
      codes.push(ukRegion);
    }
  }

  return codes;
}

export function getCoveredCountriesConsideringUKNations(
  residences: readonly { address?: Residence["address"] }[] | undefined
): string[] {
  const set = new Set<string>();

  for (const r of residences ?? []) {
    for (const code of getCoveredLocationCodesForAddress(r?.address)) {
      set.add(code);
    }
  }

  return [...set];
}

export function checkResidencesMeetRequirementsForUkSubdivision(
  residences: Residence[],
  when: {
    months?: number;
    days?: number;
  },
  subdivisionCode: string
) {
  const periodStart = getPeriodStart(
    when.months
      ? { type: "months", value: when.months }
      : { type: "days", value: when.days || 1 }
  );
  const transformedResidences = getTransformedResidences(
    residences,
    periodStart
  );

  return transformedResidences.some(
    (residence) =>
      residence.address.country === "GB" &&
      resolveGbAdminDivisionToUkRegion(
        residence.address.administrative_division_1
      ) === subdivisionCode
  );
}

export const normalizeUsCountyName = (country: string, county: string) => {
  if (country.toUpperCase() !== "US") return county;
  return county.replace(/county/i, "").trim();
};

export function getUnmetAddressHistoryPeriodRequirement(
  residences: Residence[] | undefined,
  {
    minMonths,
    nzCurrentRegionMinMonths,
  }: {
    minMonths?: number;
    nzCurrentRegionMinMonths?: number;
  }
): boolean {
  if (!residences) {
    return Boolean(minMonths || nzCurrentRegionMinMonths);
  }

  const hasNzCurrentAddress = residences.some(
    (residence) =>
      !residence.end_date && residence.address?.country?.toUpperCase() === "NZ"
  );

  if (hasNzCurrentAddress && nzCurrentRegionMinMonths) {
    return !checkResidencesMeetRequirementsForPeriod(residences, {
      type: "months",
      value: nzCurrentRegionMinMonths,
    });
  }

  if (minMonths) {
    return !checkResidencesMeetRequirementsForPeriod(residences, {
      type: "months",
      value: minMonths,
    });
  }

  return false;
}
